import React from 'react';
import { styled } from '@mui/material/styles';
import { Box, Typography, Button, Chip, CircularProgress } from '@mui/material';

const CardContainer = styled(Box)(({ theme }) => ({
  border: '1px solid #EDF2F7',
  borderRadius: '12px',
  padding: '16px 20px',
  width: '100%',
  marginBottom: '12px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  backgroundColor: '#FFFFFF',
  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.02)',
  transition: 'all 0.2s ease',
  boxSizing: 'border-box',
  '&:hover': {
    boxShadow: '0 4px 10px rgba(0, 0, 0, 0.04)',
    borderColor: '#E2E8F0',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '12px 16px',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '12px',
  },
}));

const TestInfo = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
});

const TestTitle = styled(Typography)({
  fontSize: '15px',
  fontWeight: 700,
  color: '#2D3748',
  marginBottom: '4px',
  fontFamily: "'Poppins', 'Inter', sans-serif",
});

const TestTime = styled(Typography)({
  fontSize: '13px',
  fontWeight: 600,
  color: '#718096',
  fontFamily: "'Poppins', 'Inter', sans-serif",
});

const DifficultyChip = styled(Chip)(({ level }) => ({
  backgroundColor: level === 'Easy' ? '#E6FFFA' : level === 'Medium' ? '#FFFBEB' : '#FFF5F5',
  color: level === 'Easy' ? '#319795' : level === 'Medium' ? '#D69E2E' : '#E53E3E',
  height: '22px',
  fontSize: '11px',
  fontWeight: 800,
  borderRadius: '6px',
  marginRight: '12px',
  fontFamily: "'Poppins', 'Inter', sans-serif",
  '& .MuiChip-label': {
    padding: '0 8px',
  },
}));

const StartButton = styled(Button)(({ theme, isLocked }) => ({
  backgroundColor: isLocked ? '#C6CBD9' : '#996CFE',
  color: 'white',
  borderRadius: '10px',
  padding: '6px 20px',
  fontSize: '13px',
  fontWeight: 800,
  textTransform: 'uppercase',
  minWidth: '90px',
  height: '38px',
  boxShadow: isLocked ? 'none' : '0 4px 6px rgba(153, 108, 254, 0.15)',
  fontFamily: "'Poppins', 'Inter', sans-serif",
  cursor: isLocked ? 'not-allowed' : 'pointer',
  '&:hover': {
    backgroundColor: isLocked ? '#C6CBD9' : '#7C4DFF',
    boxShadow: isLocked ? 'none' : '0 6px 12px rgba(153, 108, 254, 0.25)',
  },
  [theme.breakpoints.down('sm')]: {
    minWidth: '70px',
    padding: '4px 12px',
  },
}));

const OutlinedStartButton = styled(Button)(({ theme, isLocked }) => ({
  borderRadius: '10px',
  padding: '6px 20px',
  fontSize: '13px',
  fontWeight: 800,
  textTransform: 'uppercase',
  minWidth: '90px',
  height: '38px',
  fontFamily: "'Poppins', 'Inter', sans-serif",
  borderColor: isLocked ? '#C6CBD9' : '#996CFE',
  color: isLocked ? '#A0AEC0' : '#996CFE',
  backgroundColor: '#fff',
  cursor: isLocked ? 'not-allowed' : 'pointer',
  '&:hover': {
    borderColor: isLocked ? '#C6CBD9' : '#7C4DFF',
    backgroundColor: isLocked ? '#fff' : 'rgba(153, 108, 254, 0.06)',
  },
  [theme.breakpoints.down('sm')]: {
    minWidth: '70px',
    padding: '4px 12px',
  },
}));

const ActionsWrap = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: '8px',
  flexWrap: 'wrap',
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    justifyContent: 'flex-end',
  },
}));

const TestCard = ({
  title,
  time,
  difficulty,
  onStart,
  onResume,
  showResumeButton = false,
  loading = false,
  loadingStart = false,
  loadingResume = false,
  isLocked = false,
  disabledMessage = null,
  startLabel = 'Start',
}) => {
  const isDisabled = isLocked || !!disabledMessage;
  const startPending = showResumeButton ? loadingStart : loadingStart || loading;
  const resumePending = loadingResume;
  
  return (
    <CardContainer>
      <TestInfo>
        <Box display="flex" alignItems="center" mb={0.5}>
          <TestTitle>{title}</TestTitle>
        </Box>
        <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
          <DifficultyChip label={difficulty} level={difficulty} size="small" />
          <TestTime>{time}</TestTime>
          {title.includes('Complete IELTS Test') && (
            <Chip 
              label="Speaking Optional" 
              size="small" 
              variant="outlined"
              sx={{ 
                fontSize: '10px', 
                height: '20px',
                color: '#996CFE',
                borderColor: '#996CFE'
              }} 
            />
          )}
        </Box>
        {disabledMessage && (
          <Typography 
            sx={{ 
              fontSize: '11px', 
              color: '#E53E3E', 
              fontWeight: 600,
              mt: 0.5,
              fontFamily: "'Poppins', 'Inter', sans-serif"
            }}
          >
            {disabledMessage}
          </Typography>
        )}
      </TestInfo>
      {showResumeButton && onResume ? (
        <ActionsWrap>
          <OutlinedStartButton
            variant="outlined"
            onClick={isDisabled ? undefined : onStart}
            disabled={startPending || resumePending || isDisabled}
            isLocked={isDisabled}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
              {startPending ? (
                <CircularProgress size={16} sx={{ color: '#996CFE' }} />
              ) : (
                isDisabled ? (isLocked ? 'Locked' : 'Disabled') : startLabel
              )}
            </Box>
          </OutlinedStartButton>
          <StartButton
            onClick={isDisabled ? undefined : onResume}
            disabled={startPending || resumePending || isDisabled}
            isLocked={isDisabled}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
              {resumePending ? (
                <CircularProgress size={16} sx={{ color: 'white' }} />
              ) : (
                'Resume'
              )}
            </Box>
          </StartButton>
        </ActionsWrap>
      ) : (
        <StartButton onClick={isDisabled ? undefined : onStart} disabled={startPending || isDisabled} isLocked={isDisabled}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
            {startPending ? (
              <CircularProgress 
                size={16} 
                sx={{ color: 'white' }} 
              />
            ) : (
              isDisabled ? (isLocked ? 'Locked' : 'Disabled') : startLabel
            )}
          </Box>
        </StartButton>
      )}
    </CardContainer>
  );
};

export default TestCard; 